import { Text, Tooltip, VStack } from "@chakra-ui/react";
import { Trans, t } from "@lingui/macro";
import type { SortOrder as GraphqlSortOrder } from "@src/__generated__/graphql";
import type {
  BrandRevenueOrderByColumnEnum,
  SortOrder as UrqlSortOrder,
} from "@src/__generated__/urql-graphql";
import { BrandRevenueModel } from "@src/components/modules/reports/brand/revenue/models";
import {
  ACTIONS_COLUMN_ID,
  ColoredAmount,
  DataTable,
} from "@src/components/ui-kit";
import { currency, percent } from "@src/utils/formatters";
import { observer } from "mobx-react-lite";
import { useMemo } from "react";
import { ProfitAndRevenueAmount } from "../../../common/components";
import { createBrandColumns, withBrandHeaderTooltip } from "../../common";
import type {
  BrandProfitReportColumn,
  BrandProfitReportRow,
} from "../../common/types";
import { useStore } from "../store";

const NA = () => (
  <Tooltip
    label={t`Some budget item is still open. Cannot calculate the profit.`}
  >
    <Text pr="5" color="grey.500">
      <Trans>N/A</Trans>
    </Text>
  </Tooltip>
);

export const Table = observer(function Table() {
  const store = useStore();
  const columns = useMemo<BrandProfitReportColumn[]>(() => {
    const cols = createBrandColumns<BrandProfitReportRow>({
      detailPathname: "/reports/profitability-by-project",
      from: store.reportStore.dateRange.start,
      to: store.reportStore.dateRange.end,
      groupBy: store.reportStore.groupBy,
    });

    cols.push({
      id: "_ourWorkIncome",
      meta: { label: t`Work Profit`, align: "right" },
      header: withBrandHeaderTooltip(
        t`Work Profit`,
        <Trans>
          Invoiced amount for work minus actual labor costs and assigned work
          expenses.
        </Trans>,
      ),
      cell: ({ row }) => {
        if (row.original instanceof BrandRevenueModel) {
          return null;
        }

        return (
          <ProfitAndRevenueAmount
            amount={row.original.our_work_revenue_for_date_range}
          />
        );
      },
    });

    store.reportStore.expenseBudgetCategories.forEach((expCategory) => {
      cols.push({
        id: `profit_${expCategory.id}`,
        meta: {
          label: `${expCategory.name} ${t`Profit`}`,
          align: "right",
        },
        header: withBrandHeaderTooltip(
          `${expCategory.name} ${t`Profit`}`,
          <Text>
            {t`Invoiced amount for ${expCategory.name.toLowerCase()} minus received ${expCategory.name.toLowerCase()} expenses, calculated for selected period.`}
          </Text>,
        ),
        cell: ({ row }) => {
          if (row.original instanceof BrandRevenueModel) {
            return null;
          }

          const profit = row.original.expenses_profit_for_date_range.find(
            (category) =>
              category.expense_budget_category_id === expCategory.id,
          )?.profit;

          return typeof profit === "number" ? (
            <ProfitAndRevenueAmount amount={profit} />
          ) : (
            <NA />
          );
        },
      });
    });

    cols.push(
      {
        id: "_ourGrossIncome",
        meta: { label: t`Gross Income`, align: "right" },
        header: withBrandHeaderTooltip(
          t`Gross Income`,
          <Trans>
            Sum of Work Invoiced plus Production, Media and Royalties Profit.
          </Trans>,
        ),
        cell: ({ row }) => (
          <ProfitAndRevenueAmount
            amount={row.original.gross_income_for_date_range}
          />
        ),
      },
      {
        id: "_ourGrossProfit",
        meta: { label: t`Total Profit`, align: "right" },
        header: withBrandHeaderTooltip(
          t`Total Profit`,
          <Trans>
            It is summary of Work profit + Production Profit + Media Profit
          </Trans>,
        ),
        cell: ({ row }) => {
          if (row.original instanceof BrandRevenueModel) {
            return null;
          }

          return (
            <Text pr="5">
              <ColoredAmount value={row.original.gross_profit_for_date_range}>
                {currency.formatByWorkspace(
                  row.original.gross_profit_for_date_range,
                )}
              </ColoredAmount>
            </Text>
          );
        },
      },
      {
        id: "profit-share",
        meta: { label: t`Share Profit`, align: "right" },
        header: withBrandHeaderTooltip(
          t`Share Profit`,
          <Trans>
            Percentage of total profits for the selected period and applied
            filters.
          </Trans>,
        ),
        cell: ({ row }) => {
          if (row.original instanceof BrandRevenueModel) {
            return null;
          }

          return (
            <VStack align="right" pr="5">
              <Text>{percent.format(row.original.filtered_profit_share)}</Text>
              <Text color="gray.400">
                {percent.format(row.original.total_profit_share)}
              </Text>
            </VStack>
          );
        },
      },
      {
        id: ACTIONS_COLUMN_ID,
      },
    );

    return cols;
  }, [
    store.reportStore.dateRange.end,
    store.reportStore.dateRange.start,
    store.reportStore.expenseBudgetCategories,
    store.reportStore.groupBy,
  ]);

  const orderedBy = store.reportStore.orderBy.clauses[0];

  return (
    <DataTable.Body<BrandProfitReportRow, BrandRevenueOrderByColumnEnum>
      tableKey={store.key}
      data={store.brands as BrandProfitReportRow[]}
      columns={columns}
      orderedBy={
        orderedBy
          ? (() => {
              const order = orderedBy.order as GraphqlSortOrder | undefined;

              if (!order) {
                return undefined;
              }

              return {
                column: orderedBy.column,
                order,
              };
            })()
          : undefined
      }
      onOrderByChange={(clause) => {
        if (!clause.order) {
          store.reportStore.orderBy.setClauses([]);
          return;
        }

        const order = clause.order as UrqlSortOrder | undefined;

        if (!order) {
          store.reportStore.orderBy.setClauses([]);
          return;
        }

        store.reportStore.orderBy.setClauses([
          { column: clause.column, order },
        ]);
      }}
    />
  );
});
